import { ReactNode, useState } from "react";
import { Link } from "wouter";
import Sidebar from "@/components/sidebar";
import MobileHeader from "@/components/mobile-header";
import { useSidebarCollapsed } from "@/hooks/useSidebarCollapsed";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { X, Clock } from "lucide-react";

interface LayoutProps {
  children: ReactNode;
  onCreateGoal?: () => void;
}

function TrialBanner() {
  const { tenant } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;
  if (tenant?.subscriptionStatus !== "trialing") return null;
  if (!tenant?.trialEndsAt) return null;

  const trialEnd = new Date(tenant.trialEndsAt);
  const now = new Date();
  if (trialEnd <= now) return null; // Trial already expired — handled by redirect

  const msLeft = trialEnd.getTime() - now.getTime();
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
  const label = daysLeft === 1 ? "1 day" : `${daysLeft} days`;

  return (
    <div className="bg-amber-500 text-white text-sm px-4 py-2 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 flex-shrink-0" />
        <span>
          <strong>Free trial — {label} remaining.</strong> Explore every feature at no charge.
        </span>
      </div>
      <div className="flex items-center gap-3">
        <Link href="/pricing">
          <Button size="sm" variant="secondary" className="h-7 text-xs px-3 bg-white text-amber-700 hover:bg-amber-50">
            Choose a plan
          </Button>
        </Link>
        <button
          onClick={() => setDismissed(true)}
          className="opacity-80 hover:opacity-100 transition-opacity"
          aria-label="Dismiss trial banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children, onCreateGoal }: LayoutProps) {
  const { isCollapsed } = useSidebarCollapsed();
  
  return (
    <div className="min-h-screen flex bg-background overflow-x-hidden">
      <Sidebar />
      <MobileHeader onCreateGoal={onCreateGoal || (() => {})} />

      <div className={`flex flex-col flex-1 w-full min-w-0 transition-all duration-300 ${
        isCollapsed ? 'lg:pl-[72px]' : 'lg:pl-64'
      }`}>
        <TrialBanner />
        <main className="flex-1 pb-4 md:pb-8 px-4 md:px-6 lg:px-8 lg:pl-0 pt-20 lg:pt-6">
          {children}
        </main>
      </div>
    </div>
  );
}
